<script setup lang="ts">
import { map } from "lodash-es";
import { NavArrowRight } from "@iconoir/vue";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";

defineOptions({ name: "RevenueReport" });

const { t } = useI18n();
const { c, n } = useFormat();
const { $api } = useNuxtApp();
const toast = useToast();

interface RevenueSeriesRecord {
  id: string;
  label: string;
  start?: number;
  revenue: number;
  orders: number;
  pending: number;
}
interface RevenueAnalytics {
  overview: {
    currency: string;
    totalRevenue: number;
    paidRevenue: number;
    outstandingRevenue: number;
    orderCount: number;
    averageOrderValue: number;
  };
  series: RevenueSeriesRecord[];
}

const timezone = "Asia/Ho_Chi_Minh";
// Matches the picker's "Last 30 days" preset exactly, so the field shows
// that label and leaves its row active.
const filter = reactive({
  from: moment.tz(timezone).subtract(29, "days").startOf("day").unix(),
  to: moment.tz(timezone).endOf("day").unix(),
  groupBy: "week",
});
// Calendar-aligned, unlike the picker's own rolling day windows: comparing two
// periods only means anything when both are whole months. Twelve is the
// ceiling — the revenue and usage pipelines walk a day at a time and reject a
// range past 367 days.
const presets = computed(() => {
  const monthsBack = (count: number): [number, number] => [
    moment.tz(timezone).subtract(count - 1, "months").startOf("month").unix(),
    moment.tz(timezone).endOf("day").unix(),
  ];
  return [
    { label: t("dateRange.last3Months"), getValue: () => monthsBack(3) },
    { label: t("dateRange.last6Months"), getValue: () => monthsBack(6) },
    { label: t("dateRange.last12Months"), getValue: () => monthsBack(12) },
  ];
});

const query = computed(() => ({
  ...(filter.from ? { from: filter.from } : {}),
  ...(filter.to ? { to: filter.to } : {}),
  groupBy: filter.groupBy,
}));
const groupByItems = computed(() => [
  { value: "week", label: t("analytics.groupBy.week") },
  { value: "month", label: t("analytics.groupBy.month") },
  { value: "year", label: t("analytics.groupBy.year") },
]);

// The two controls are otherwise independent, which lets a range and a
// granularity combine into a chart of one bar — or a request the backend
// refuses outright. This only intervenes when the current choice would produce
// something unreadable; between two and sixty buckets it leaves it alone.
const bucketCount = (groupBy: string, from: number, to: number) => {
  const unit = groupBy === "year" ? "years" : groupBy === "month" ? "months" : "weeks";
  return moment.unix(to).diff(moment.unix(from), unit) + 1;
};

watch(
  () => [filter.from, filter.to],
  () => {
    const current = bucketCount(filter.groupBy, filter.from, filter.to);
    if (current >= 2 && current <= 60) return;
    // Coarsest first: a year of data reads as twelve months, not fifty-one
    // weeks. Three buckets is the floor for a shape to be a trend at all.
    const options = groupByItems.value.map((item) => item.value).reverse();
    const counts = options.map((value) => bucketCount(value, filter.from, filter.to));
    const fit =
      options.find((_, i) => counts[i]! >= 3 && counts[i]! <= 60) ??
      options.findLast((_, i) => counts[i]! <= 60);
    if (fit && fit !== filter.groupBy) filter.groupBy = fit;
  },
);

const firstLoading = ref(true);
// firstLoading only ever falls once; this one tracks every refetch too, so a
// filter change shows the charts working instead of sitting on stale numbers.
const loading = ref(false);
const data = ref<RevenueAnalytics | null>(null);
const byType = ref<RevenueSeriesRecord[]>([]);
const byPlan = ref<RevenueSeriesRecord[]>([]);
const byWorkspace = ref<RevenueSeriesRecord[]>([]);

const labels = computed(() => map(data.value?.series ?? [], "label"));

const trendChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("analytics.chart.revenue"), t("analytics.chart.orderCount")] },
  xAxis: { type: "category", data: labels.value },
  yAxis: [
    { type: "value" },
    { type: "value", minInterval: 1 },
  ],
  series: [
    { name: t("analytics.chart.revenue"), type: "bar", data: map(data.value?.series ?? [], "revenue") },
    { name: t("analytics.chart.orderCount"), type: "line", yAxisIndex: 1, data: map(data.value?.series ?? [], "orders") },
  ],
}));

// Slices carry no labels, so the legend is what names them — a theme cannot
// add one, the option has to ask for it.
const typeChartOption = computed(() => ({
  tooltip: { trigger: "item" },
  legend: {},
  series: [
    {
      type: "pie",
      data: map(byType.value, (r) => ({
        name: t(`analytics.type_${r.id}`),
        value: Math.round(r.revenue * 100) / 100,
      })),
    },
  ],
}));

// Ranked bars read top-down, and a category axis draws bottom-up, so both the
// labels and their values are reversed together.
const planChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  xAxis: { type: "value" },
  yAxis: { type: "category", data: map(byPlan.value, "label").reverse() },
  series: [
    { type: "bar", data: map(byPlan.value, (r) => Math.round(r.revenue * 100) / 100).reverse() },
  ],
}));

// The time chart and the three entity charts are the same endpoint on different
// axes, so they load together; the API shares one cached total between them.
async function load() {
  loading.value = true;
  try {
    const [trend, type, plan, workspace] = await Promise.all([
      $api<RevenueAnalytics>("/analytics/revenue", { query: query.value }),
      $api<RevenueAnalytics>("/analytics/revenue", { query: { ...query.value, groupBy: "type" } }),
      $api<RevenueAnalytics>("/analytics/revenue", { query: { ...query.value, groupBy: "plan" } }),
      $api<RevenueAnalytics>("/analytics/revenue", { query: { ...query.value, groupBy: "workspace" } }),
    ]);
    data.value = trend;
    byType.value = type.series;
    byPlan.value = plan.series;
    byWorkspace.value = workspace.series;
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

watch(query, () => {
  load();
});

onMounted(async () => {
  await load();
});
</script>

<template>
  <div class="flex flex-col gap-large">
    <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
      <SegmentedControl v-model="filter.groupBy" :items="groupByItems" size="small" />
      <FormDateRange
        v-model:from="filter.from"
        v-model:to="filter.to"
        :timezone="timezone"
        :presets="presets"
        size="sm"
      />
    </div>

    <div class="grid grid-cols-2 gap-medium md:grid-cols-3 lg:grid-cols-5">
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.totalRevenue')"
        :value="c(data?.overview.totalRevenue ?? 0, 'USD')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.paidRevenue')"
        :value="c(data?.overview.paidRevenue ?? 0, 'USD')"
        intent="positive"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.outstandingRevenue')"
        :value="c(data?.overview.outstandingRevenue ?? 0, 'USD')"
        :intent="(data?.overview.outstandingRevenue ?? 0) > 0 ? 'negative' : 'neutral'"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.orders')"
        :value="n(data?.overview.orderCount ?? 0, 'integer')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.aov')"
        :value="c(data?.overview.averageOrderValue ?? 0, 'USD')"
      />
    </div>

    <SectionCard :title="t('analytics.chart.revenueTrend')">
      <Chart :option="trendChartOption" :loading="loading" :height="300" />
    </SectionCard>

    <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
      <SectionCard :title="t('analytics.chart.byType')">
        <Chart :option="typeChartOption" :loading="loading" />
      </SectionCard>
      <SectionCard :title="t('analytics.chart.byPlan')">
        <Chart :option="planChartOption" :loading="loading" />
      </SectionCard>
    </div>

    <Card v-if="firstLoading || byWorkspace.length">
      <DataTable
        :columns="[
          { key: 'label', label: t('field.name'), class: 'md:min-w-[240px]' },
          { key: 'orders', label: t('analytics.chart.orderCount'), align: 'right', class: 'min-w-[112px]' },
          { key: 'revenue', label: t('analytics.chart.revenue'), align: 'right', class: 'min-w-[160px]' },
          { key: 'actions', label: '', align: 'right', class: 'min-w-[64px]' },
        ]"
        :items="byWorkspace"
        row-key="id"
      >
        <template #cell-label="{ row }">
          <TableLink :to="`/workspace/${row.id}`">{{ row.label }}</TableLink>
        </template>
        <template #cell-orders="{ row }">
          {{ n(row.orders, "integer") }}
        </template>
        <template #cell-revenue="{ row }">
          <span class="content-neutral-strong">{{ c(row.revenue, "USD") }}</span>
        </template>
        <template #actions="{ row }">
          <NuxtLink :to="`/workspace/${row.id}`" class="inline-flex" :aria-label="row.label">
            <ButtonIcon variant="flat-neutral" size="small">
              <NavArrowRight class="icon" />
            </ButtonIcon>
          </NuxtLink>
        </template>
      </DataTable>
    </Card>
  </div>
</template>
